import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  LogOut, 
  Shield, 
  Menu, 
  X, 
  UserPlus, 
  ShoppingBag,
  Users,
  Briefcase,
  FileText,
  Award,
  UserCheck,
  BookOpen,
  CheckCircle,
  Star,
  ShoppingCart,
  Database,
  HeartPulse,
  ChevronDown,
  ChevronUp,
  Info,
  TrendingUp,
  DollarSign,
  Handshake,
  Trophy,
  Building,
  Newspaper,
  Book,
  FileText as FileTextIcon,
  CheckSquare,
  ChevronRight,
  CreditCard,
  Users as MembersIcon,
  ClipboardList   // NEW ICON FOR DRAP APPLICATIONS
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = "https://backend.dlapakistan.com/api";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Main About DLA section state
  const [isAboutSectionOpen, setIsAboutSectionOpen] = useState(true);

  // DLA Trade section state
  const [isTradeSectionOpen, setIsTradeSectionOpen] = useState(false);

  // DLA Shop section state
  const [isShopSectionOpen, setIsShopSectionOpen] = useState(false);

  // Dental News section state - default CLOSED
  const [isDentalNewsSectionOpen, setIsDentalNewsSectionOpen] = useState(false);

  // Become a Member section state
  const [isBecomeMemberSectionOpen, setIsBecomeMemberSectionOpen] = useState(false);

  // Dental news items state
  const [dentalNewsItems, setDentalNewsItems] = useState([]);
  const [isLoadingDentalNews, setIsLoadingDentalNews] = useState(false);
  const [errorLoadingNews, setErrorLoadingNews] = useState(null);

  // Become a Member posts state
  const [becomeMemberPosts, setBecomeMemberPosts] = useState([]);
  const [becomeMemberContents, setBecomeMemberContents] = useState([]);
  const [isLoadingBecomeMember, setIsLoadingBecomeMember] = useState(false);

  // DLA Shop items state
  const [dlaShopItems, setDlaShopItems] = useState([]);
  const [isLoadingDlaShop, setIsLoadingDlaShop] = useState(false);
  const [errorLoadingDlaShop, setErrorLoadingDlaShop] = useState(null);

  // Original sub-menu states
  const [openSubmenus, setOpenSubmenus] = useState({
    'work-we-do': false,
    'our-policies': false,
    'become-member': false,
    'dla-fellowship': false,
    'dla-trade': false
  });

  // Define functions BEFORE using them in effects
  const isActivePath = (path) =>
    location.pathname === path || location.pathname.startsWith(path);

  // Check if any About route is active
  const isAnyAboutRouteActive = () => {
    return isActivePath('/About-Dla') ||
      location.pathname.includes('/WorkWeDo') ||
      location.pathname.includes('/OurPolicies') ||
      location.pathname.includes('/BecomeMember') ||
      location.pathname.includes('/DLAFellowship') ||
      location.pathname.includes('/Fellowship');
  };

  // Check if any DLA Trade route is active
  const isAnyTradeRouteActive = () => {
    return isActivePath('/DlaTrade') ||
      location.pathname.includes('/headline-sponsor') ||
      location.pathname.includes('/sponsor-opportunity') ||
      location.pathname.includes('/trade-awards') ||
      location.pathname.includes('/az-affiliates');
  };

  // Check if any Shop route is active
  const isAnyShopRouteActive = () => {
    return location.pathname.startsWith('/dla-shop');
  };

  // Check if any Dental News route is active
  const isAnyDentalNewsRouteActive = () => {
    return location.pathname.startsWith('/dental-news') &&
           !location.pathname.includes('/dentallab-journal');
  };

  // Check if any Become Member route is active
  const isAnyBecomeMemberRouteActive = () => {
    return location.pathname.includes('/BecomeMember');
  };

  // Check if Dentallab Journal route is active
  const isDentallabJournalActive = () => {
    return location.pathname.includes('/dentallab-journal');
  };

  // Check if Add Store Cards route is active
  const isAddStoreCardsActive = () => {
    return location.pathname === '/add-store-cards';
  };

  // Check if Add Members Area Content route is active
  const isAddMembersAreaActive = () => {
    return location.pathname === '/add-members-area';
  };

  // NEW: Check if DRAP Licence Applications route is active
  const isDrapApplicationsActive = () => {
    return location.pathname === '/drap-applications';
  };

  // Check if a specific dental news item posts page is active
  const isDentalNewsItemPostsActive = (itemId) => {
    return location.pathname === `/dental-news/${itemId}/posts`;
  };

  // Check if we're on a become member content page
  const isBecomeMemberContentActive = (slug) => {
    return location.pathname === `/About-Dla/BecomeMember/content/${slug}`;
  };

  // Check if it's the "All Products" page
  const isAllProductsPage = () => {
    return location.pathname === '/dla-shop' || location.pathname === '/dla-shop';
  };

  // Detect screen size
  useEffect(() => {
    const checkScreen = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };

    checkScreen();
    window.addEventListener('resize', checkScreen);
    return () => window.removeEventListener('resize', checkScreen);
  }, []);

  // Fetch dental news items from backend - FIXED with better error handling
  const fetchDentalNews = async () => {
    setIsLoadingDentalNews(true);
    setErrorLoadingNews(null);
    try {
      console.log("Fetching dental news from:", `${API_BASE_URL}/dental-news/`);
      const response = await axios.get(`${API_BASE_URL}/dental-news/`);
      console.log("Dental news response:", response.data);

      // Handle different response structures
      let newsData = [];
      if (Array.isArray(response.data)) {
        newsData = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        newsData = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        newsData = response.data.data;
      } else if (response.data.news && Array.isArray(response.data.news)) {
        newsData = response.data.news;
      }

      // Sort by created_at date (oldest first)
      const sortedNews = newsData.sort((a, b) =>
        new Date(a.created_at) - new Date(b.created_at)
      );

      console.log("Processed dental news items:", sortedNews);
      setDentalNewsItems(sortedNews);
    } catch (err) {
      console.error("Error fetching dental news:", err);
      setErrorLoadingNews("Failed to load dental news");
      setDentalNewsItems([]);
    } finally {
      setIsLoadingDentalNews(false);
    }
  };

  // Fetch DLA Shop categories from backend - FIXED with better error handling
  const fetchDlaShopCategories = async () => {
    setIsLoadingDlaShop(true);
    setErrorLoadingDlaShop(null);
    try {
      console.log("Fetching DLA shop categories from:", `${API_BASE_URL}/dla-shop/categories/`);
      const response = await axios.get(`${API_BASE_URL}/dla-shop/categories/`);
      console.log("Response data:", response.data);

      // Handle different response structures
      let items = [];
      if (Array.isArray(response.data)) {
        items = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        items = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        items = response.data.data;
      } else if (response.data.categories && Array.isArray(response.data.categories)) {
        items = response.data.categories;
      }

      console.log("Processed items:", items);
      setDlaShopItems(items);
    } catch (err) {
      console.error("Error fetching DLA shop categories:", err);
      setErrorLoadingDlaShop("Failed to load DLA shop categories");
      setDlaShopItems([]);
    } finally {
      setIsLoadingDlaShop(false);
    }
  };

  // Fetch Become a Member posts from API - FIXED with array checks
  const fetchBecomeMemberPosts = async () => {
    setIsLoadingBecomeMember(true);
    try {
      const postsResponse = await axios.get(`${API_BASE_URL}/member/posts/`);

      // Ensure we always set arrays
      setBecomeMemberPosts(Array.isArray(postsResponse.data) ? postsResponse.data : []);

      const contentsResponse = await axios.get(`${API_BASE_URL}/member/contents/`);
      setBecomeMemberContents(Array.isArray(contentsResponse.data) ? contentsResponse.data : []);

    } catch (err) {
      console.error("Error fetching become member posts:", err);
      setBecomeMemberPosts([]);
      setBecomeMemberContents([]);
    } finally {
      setIsLoadingBecomeMember(false);
    }
  };

  // Fetch initial data when component mounts
  useEffect(() => {
    // Fetch all data when sidebar mounts
    fetchDentalNews();
    fetchDlaShopCategories();
    fetchBecomeMemberPosts();
  }, []);

  // Auto-open sections based on active route AND fetch data
  useEffect(() => {
    const path = location.pathname;

    const newOpenSubmenus = { ...openSubmenus };

    // About section submenus
    if (path.includes('/WorkWeDo')) {
      newOpenSubmenus['work-we-do'] = true;
      setIsAboutSectionOpen(true);
    }
    if (path.includes('/OurPolicies')) {
      newOpenSubmenus['our-policies'] = true;
      setIsAboutSectionOpen(true);
    }
    if (path.includes('/BecomeMember')) {
      newOpenSubmenus['become-member'] = true;
      setIsAboutSectionOpen(true);
      // Also open Become Member section
      setIsBecomeMemberSectionOpen(true);
      // Fetch become member posts
      fetchBecomeMemberPosts();
    }
    if (path.includes('/DLAFellowship') || path.includes('/Fellowship')) {
      newOpenSubmenus['dla-fellowship'] = true;
      setIsAboutSectionOpen(true);
    }

    // DLA Trade section
    if (path.includes('/DlaTrade') ||
        path.includes('/headline-sponsor') ||
        path.includes('/sponsor-opportunity') ||
        path.includes('/trade-awards') ||
        path.includes('/az-affiliates')) {
      newOpenSubmenus['dla-trade'] = true;
      setIsTradeSectionOpen(true);
    }

    // Shop section auto-open if any Shop route is active
    if (isAnyShopRouteActive()) {
      setIsShopSectionOpen(true);
    }

    // Dental News section auto-open if any Dental News route is active
    if (isAnyDentalNewsRouteActive()) {
      setIsDentalNewsSectionOpen(true);
      fetchDentalNews();
    }

    // Become Member section auto-open if any Become Member route is active
    if (isAnyBecomeMemberRouteActive()) {
      setIsBecomeMemberSectionOpen(true);
      fetchBecomeMemberPosts();
    }

    setOpenSubmenus(newOpenSubmenus);
  }, [location.pathname]);

  // Fetch data when sidebar opens and sections are active
  useEffect(() => {
    if (isSidebarOpen) {
      console.log("Sidebar opened, checking what to fetch");
      if (isAnyDentalNewsRouteActive()) {
        fetchDentalNews();
      }
      if (isShopSectionOpen || isAnyShopRouteActive()) {
        console.log("Fetching shop categories");
        fetchDlaShopCategories();
      }
      if (isAnyBecomeMemberRouteActive()) {
        fetchBecomeMemberPosts();
      }
    }
  }, [isSidebarOpen, isShopSectionOpen]);

  // Toggle Shop section - fetch data when opening
  const toggleShopSection = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    const newState = !isShopSectionOpen;
    setIsShopSectionOpen(newState);

    // Fetch DLA shop categories when opening the section
    if (newState && isSidebarOpen) {
      fetchDlaShopCategories();
    }
  };

  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  // Handle Add Store Cards button click
  const handleAddStoreCards = () => {
    if (isMobile) setIsSidebarOpen(false);
    navigate('/add-store-cards');
  };

  // Handle Add Members Area button click
  const handleAddMembersArea = () => {
    if (isMobile) setIsSidebarOpen(false);
    navigate('/add-members-area');
  };

  // Toggle sections
  const toggleAboutSection = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsAboutSectionOpen(!isAboutSectionOpen);
  };

  const toggleTradeSection = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsTradeSectionOpen(!isTradeSectionOpen);
  };

  // Toggle Dental News section
  const toggleDentalNewsSection = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newState = !isDentalNewsSectionOpen;
    setIsDentalNewsSectionOpen(newState);

    // Fetch dental news when opening the section
    if (newState && isSidebarOpen) {
      fetchDentalNews();
    }
  };

  // Toggle Become Member section
  const toggleBecomeMemberSection = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newState = !isBecomeMemberSectionOpen;
    setIsBecomeMemberSectionOpen(newState);

    // Fetch become member posts when opening the section
    if (newState && isSidebarOpen) {
      fetchBecomeMemberPosts();
    }
  };

  // Toggle submenu
  const toggleSubmenu = (menuId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setOpenSubmenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Handle clicking on dental news title to navigate to its posts page
  const handleDentalNewsTitleClick = (item) => {
    if (isMobile) setIsSidebarOpen(false);

    // Save the news data to localStorage for the posts page
    localStorage.setItem('current_dental_news', JSON.stringify({
      id: item.id,
      title: item.title
    }));

    // Navigate to the posts page for this news item
    navigate(`/dental-news/${item.id}/posts`);
  };

  // Handle clicking on DLA Shop category to navigate
  const handleDlaShopCategoryClick = (item) => {
    if (isMobile) setIsSidebarOpen(false);

    // Navigate to the DLA Shop category page
    navigate(`/dla-shop/${item.id}`);
  };

  // Handle clicking on become member button to navigate to content page
  const handleBecomeMemberButtonClick = (buttonItem) => {
    if (isMobile) setIsSidebarOpen(false);

    if (buttonItem.content && buttonItem.content.slug) {
      // Navigate to content page with slug
      navigate(`/About-Dla/BecomeMember/content/${buttonItem.content.slug}`);
    } else if (buttonItem.post && buttonItem.post.slug) {
      // If no content, navigate to post details
      navigate(`/About-Dla/BecomeMember/content/${buttonItem.post.slug}`);
    } else {
      // Show message if no content available
      alert(`No content available for "${buttonItem.text}"`);
    }
  };

  // Get unique buttons from posts - FIXED with array checks
  const getBecomeMemberButtons = () => {
    // Safety checks
    if (!Array.isArray(becomeMemberPosts)) {
      console.log('becomeMemberPosts is not an array:', becomeMemberPosts);
      return [];
    }

    if (!Array.isArray(becomeMemberContents)) {
      console.log('becomeMemberContents is not an array:', becomeMemberContents);
      return [];
    }

    const postsWithButtons = becomeMemberPosts.filter(post => post && post.button && post.button.trim() !== "");
    const uniqueButtons = [...new Set(postsWithButtons.map(post => post.button.trim()))];

    return uniqueButtons.map(buttonName => {
      const postWithButton = postsWithButtons.find(post => post.button === buttonName);
      const contentForButton = becomeMemberContents.find(content => content && content.button === buttonName);

      return {
        text: buttonName,
        hasContent: !!contentForButton,
        post: postWithButton || null,
        content: contentForButton || null,
        slug: contentForButton?.slug || postWithButton?.slug || null
      };
    });
  };

  // About menu items with submenus (PERFECT COPY FROM FIRST SIDEBAR)
  const aboutMenuItems = [
    {
      id: 'about-main',
      text: 'About DLA Content',
      path: '/About-Dla',
      icon: Info
    },
    {
      id: 'meet-board',
      text: 'Meet the Board',
      path: '/About-Dla/MeettheBoard',
      icon: Users
    },
    {
      id: 'work-we-do',
      text: 'Work We Do',
      path: '/About-Dla/WorkWeDo',
      icon: Briefcase,
      hasChildren: true,
      children: [
        {
          id: 'education',
          text: 'Education',
          path: '/About-Dla/WorkWeDo/Education'
        },
        {
          id: 'regulation',
          text: 'Regulation',
          path: '/About-Dla/WorkWeDo/Regulation'
        },
        {
          id: 'membership-support',
          text: 'Membership Support',
          path: '/About-Dla/WorkWeDo/Support'
        }
      ]
    },
    {
      id: 'our-policies',
      text: 'Our Policies',
      path: '/About-Dla/OurPolicies',
      icon: FileText,
      hasChildren: true,
      children: [
        {
          id: 'operational',
          text: 'Operational Policies',
          path: '/About-Dla/OurPolicies/Operational'
        },
        {
          id: 'political',
          text: 'Political Policies',
          path: '/About-Dla/OurPolicies/Political'
        },
        {
          id: 'general',
          text: 'General Policies',
          path: '/About-Dla/OurPolicies/General'
        }
      ]
    },
    {
      id: 'become-member',
      text: 'Become a Member',
      path: '/About-Dla/BecomeMember',
      icon: UserCheck,
      hasChildren: false,
    },
    {
      id: 'dla-fellowship',
      text: 'DLA Fellowship',
      path: '/About-Dla/DLAFellowship',
      icon: Award,
      hasChildren: true,
      children: [
        {
          id: 'fellowship-awards',
          text: 'Fellowship Awards',
          path: '/About-Dla/Fellowship/Awards'
        },
        {
          id: 'special-recognition',
          text: 'Special Recognition',
          path: '/About-Dla/Fellowship/Recognition'
        }
      ]
    }
  ];

  // DLA Trade menu items
  const tradeMenuItems = [
    {
      id: 'headline-sponsor',
      text: 'DLA Headline Sponsor',
      path: '/DlaTrade/headline-sponsor',
      icon: DollarSign
    },
    {
      id: 'sponsorship-opportunity',
      text: 'Sponsorship Opportunity',
      path: '/DlaTrade/sponsor-opportunity',
      icon: Handshake
    },
    {
      id: 'trade-awards',
      text: 'DLA Trade Awards',
      path: '/DlaTrade/trade-awards',
      icon: Trophy
    },
    {
      id: 'az-affiliates',
      text: 'A-Z Affiliate Members',
      path: '/DlaTrade/az-affiliates',
      icon: Building
    },
    {
      id: 'Become-Affiliate-Member',
      text: 'Become an Affiliate Member',
      path: '/DlaTrade/become-affiliate-member',
      icon: Users
    },
  ];

  const becomeMemberButtons = getBecomeMemberButtons();

  return (
    <>
      {/* Mobile menu button */}
      {isMobile && !isSidebarOpen && (
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 p-3 rounded-xl bg-emerald-900 shadow-xl shadow-emerald-900/30 text-white hover:bg-emerald-700 hover:shadow-2xl transition-all"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-screen bg-white border-r border-gray-200
        shadow-[8px_0_25px_-5px_rgba(0,0,0,0.2),12px_0_30px_-8px_rgba(0,0,0,0.15)] 
        transition-transform duration-300 z-40
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${!isMobile ? 'md:translate-x-0 md:sticky md:top-0 md:left-0' : ''}
        flex flex-col w-72`}
        style={{
          height: '100vh',
          overflow: 'hidden'
        }}
      >
        <div className="p-5 h-full flex flex-col" style={{
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(209, 213, 219, 0.2) transparent',
          msOverflowStyle: 'none'
        }}>
          {/* Header */}
          <div className="mb-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Shield className="text-white w-6 h-6" />
              </div>
              <div>
                <h1 className="text-gray-800 font-bold text-lg drop-shadow-sm">DLA ADMIN</h1>
                <p className="text-emerald-600 text-xs drop-shadow-sm">Management System</p>
              </div>
            </div>

            {isMobile && (
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {/* Dashboard */}
            <NavLink
              to="/home"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isActivePath('/home') ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <Home className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">Dashboard</span>
            </NavLink>

            {/* ==================== DLA SHOP SECTION ==================== */}
            <div className="pt-2">
              <div className={`relative rounded-xl transition-all mb-1
                ${isAnyShopRouteActive() 
                  ? 'bg-emerald-50 border border-emerald-100 shadow-lg shadow-emerald-200/50' 
                  : 'bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-lg'
                }`}
              >
                {/* Clickable area that navigates to /dla-shop/all-products */}
                <NavLink
                  to="/dla-shop"
                  onClick={() => {
                    if (isMobile) setIsSidebarOpen(false);
                  }}
                  className="flex items-center justify-between px-4 py-3 no-underline cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 rounded-lg shadow-lg ${isAnyShopRouteActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-200 text-gray-600 shadow-gray-300/50'}`}>
                      <ShoppingBag className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium drop-shadow-sm ${isAnyShopRouteActive() ? 'text-emerald-600' : 'text-gray-800'} break-words whitespace-normal`}>
                        DLA Shop
                      </span>
                    </div>
                  </div>
                  <div
                    className={`transition-transform flex-shrink-0 ml-2 ${isAnyShopRouteActive() ? 'text-emerald-500' : 'text-gray-500'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleShopSection();
                    }}
                  >
                    {isShopSectionOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </NavLink>

                {isShopSectionOpen && (
                  <div className="pb-2">
                    <div className="ml-10 mr-3 border-l border-gray-200 space-y-1">
                      {/* Hardcoded "All Products" link - always at the top */}
                      <NavLink
                        to="/dla-shop/all-products"
                        onClick={() => isMobile && setIsSidebarOpen(false)}
                        className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all
                          ${isActive || isAllProductsPage()
                            ? 'text-emerald-600 font-bold bg-emerald-50' 
                            : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'}`}
                      >
                        <ShoppingBag className="w-3.5 h-3.5" />
                        <span className="break-words whitespace-normal">All Products</span>
                      </NavLink>

                      {/* Loading state */}
                      {isLoadingDlaShop && dlaShopItems.length === 0 && (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          Loading shop categories...
                        </div>
                      )}

                      {/* Error state */}
                      {errorLoadingDlaShop && dlaShopItems.length === 0 && (
                        <div className="px-3 py-2 text-[13px] text-red-400 break-words whitespace-normal">
                          {errorLoadingDlaShop}
                        </div>
                      )}

                      {/* No items state */}
                      {!isLoadingDlaShop && dlaShopItems.length === 0 && !errorLoadingDlaShop && (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          No shop categories yet
                        </div>
                      )}

                      {/* DLA Shop categories from backend - FIXED with better handling */}
                      {Array.isArray(dlaShopItems) && dlaShopItems.length > 0 ? (
                        dlaShopItems.map((item) => {
                          const isActive = location.pathname === `/dla-shop/${item.id}`;

                          return (
                            <div key={item.id || Math.random()} className="relative">
                              <div
                                onClick={() => handleDlaShopCategoryClick(item)}
                                className={`flex items-start gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all cursor-pointer
                                  ${isActive 
                                    ? 'text-emerald-600 font-bold bg-emerald-50' 
                                    : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'}`}
                              >
                                <div className="flex-shrink-0 pt-0.5">
                                  <ShoppingBag className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="break-words whitespace-normal leading-relaxed">
                                      {item.title || item.name || "Unnamed"}
                                    </span>
                                    {/* Show products count badge if available */}
                                    {item.products_count > 0 && (
                                      <span className="flex-shrink-0 text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full mt-0.5">
                                        {item.products_count}
                                      </span>
                                    )}
                                    {/* Show publish status badge */}
                                    {item.is_published === false && (
                                      <span className="flex-shrink-0 text-[10px] bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded-full mt-0.5">
                                        Draft
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : !isLoadingDlaShop && !errorLoadingDlaShop ? (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          No shop categories available
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Add Members */}
            <NavLink
              to="/add-members"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isActivePath('/add-members') ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <UserPlus className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">Add Members</span>
            </NavLink>

            {/* ==================== THE DENTALLAB JOURNAL SEPARATE BUTTON ==================== */}
            <NavLink
              to="/dental-news/dentallab-journal"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isDentallabJournalActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <Book className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">The Dentallab Journal</span>
            </NavLink>

            {/* ==================== ADD MEMBERS AREA CONTENT BUTTON - NEW BUTTON ==================== */}
            <NavLink
              to="/add-members-area"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isAddMembersAreaActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <MembersIcon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">Add Members Area</span>
            </NavLink>

            {/* ==================== DENTAL NEWS SECTION ==================== */}
            <div className="pt-2">
              <div className={`relative rounded-xl transition-all cursor-pointer mb-1
                ${isAnyDentalNewsRouteActive() 
                  ? 'bg-emerald-50 border border-emerald-100 shadow-lg shadow-emerald-200/50' 
                  : 'bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-lg'
                }`}
              >
                {/* Clickable area that navigates to /dental-news (listing page) */}
                <NavLink
                  to="/dental-news"
                  onClick={() => {
                    if (isMobile) setIsSidebarOpen(false);
                  }}
                  className="flex items-center justify-between px-4 py-3 no-underline cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 rounded-lg shadow-lg ${isAnyDentalNewsRouteActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-200 text-gray-600 shadow-gray-300/50'}`}>
                      <Newspaper className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium drop-shadow-sm ${isAnyDentalNewsRouteActive() ? 'text-emerald-600' : 'text-gray-800'} break-words whitespace-normal`}>
                        Dental News
                      </span>
                    </div>
                  </div>
                  <div
                    className={`transition-transform flex-shrink-0 ml-2 ${isAnyDentalNewsRouteActive() ? 'text-emerald-500' : 'text-gray-500'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDentalNewsSection();
                    }}
                  >
                    {isDentalNewsSectionOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </NavLink>

                {/* Dental News Sub-Menus - Only show when section is open */}
                {isDentalNewsSectionOpen && (
                  <div className="pb-2">
                    <div className="ml-10 mr-3 border-l border-gray-200 space-y-1">
                      {/* Loading state */}
                      {isLoadingDentalNews && dentalNewsItems.length === 0 && (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          Loading dental news...
                        </div>
                      )}

                      {/* Error state */}
                      {errorLoadingNews && dentalNewsItems.length === 0 && (
                        <div className="px-3 py-2 text-[13px] text-red-400 break-words whitespace-normal">
                          {errorLoadingNews}
                        </div>
                      )}

                      {/* No items state */}
                      {!isLoadingDentalNews && dentalNewsItems.length === 0 && !errorLoadingNews && (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          No dental news items yet
                        </div>
                      )}

                      {/* Dental news items from backend - FIXED with array check */}
                      {Array.isArray(dentalNewsItems) && dentalNewsItems.length > 0 ? (
                        dentalNewsItems.map((item) => {
                          const isActive = isDentalNewsItemPostsActive(item.id);

                          return (
                            <div key={item.id} className="relative">
                              <div
                                onClick={() => handleDentalNewsTitleClick(item)}
                                className={`flex items-start gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all cursor-pointer
                                  ${isActive 
                                    ? 'text-emerald-600 font-bold bg-emerald-50' 
                                    : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'}`}
                              >
                                <div className="flex-shrink-0 pt-0.5">
                                  <FileTextIcon className="w-3.5 h-3.5 text-gray-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="break-words whitespace-normal leading-relaxed">
                                      {item.title}
                                    </span>
                                    {/* Show posts count badge if available */}
                                    {item.posts_count > 0 && (
                                      <span className="flex-shrink-0 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full mt-0.5">
                                        {item.posts_count}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : !isLoadingDentalNews && !errorLoadingNews ? (
                        <div className="px-3 py-2 text-[13px] text-gray-400 break-words whitespace-normal">
                          No dental news items available
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ==================== ABOUT DLA SECTION - PERFECT COPY FROM FIRST SIDEBAR ==================== */}
            <div className="pt-2">
              <div className={`relative rounded-xl transition-all cursor-pointer mb-1
                ${isAnyAboutRouteActive() 
                  ? 'bg-emerald-50 border border-emerald-100 shadow-lg shadow-emerald-200/50' 
                  : 'bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-lg'
                }`}
              >
                <div
                  className="flex items-center justify-between px-4 py-3"
                  onClick={toggleAboutSection}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 rounded-lg shadow-lg ${isAnyAboutRouteActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-200 text-gray-600 shadow-gray-300/50'}`}>
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium drop-shadow-sm ${isAnyAboutRouteActive() ? 'text-emerald-600' : 'text-gray-800'} break-words whitespace-normal`}>
                        About DLA
                      </span>
                    </div>
                  </div>
                  <div className={`transition-transform flex-shrink-0 ml-2 ${isAnyAboutRouteActive() ? 'text-emerald-500' : 'text-gray-500'}`}>
                    {isAboutSectionOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>

                {isAboutSectionOpen && (
                  <div className="pb-2">
                    <div className="ml-10 mr-3 border-l border-gray-200 space-y-1">
                      {aboutMenuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = isActivePath(item.path) ||
                          (item.children && item.children.some(child => isActivePath(child.path)));
                        const isSubmenuOpen = openSubmenus[item.id];

                        // Special handling for Become a Member
                        if (item.id === 'become-member') {
                          return (
                            <div key={item.id} className="relative">
                              <div
                                className={`flex items-center justify-between pr-2 rounded-r-lg transition-all hover:bg-gray-50
                                  ${isActive ? 'bg-emerald-50' : ''}`}
                              >
                                <NavLink
                                  to={item.path}
                                  onClick={() => isMobile && setIsSidebarOpen(false)}
                                  className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all flex-1 min-w-0
                                  ${isActive ? 'text-emerald-600 font-bold' : 'text-gray-500 hover:text-emerald-600'}`}
                                >
                                  {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                                  <span className="break-words whitespace-normal">{item.text}</span>
                                </NavLink>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleBecomeMemberSection();
                                  }}
                                  className={`p-1 rounded hover:bg-emerald-100 flex-shrink-0 ${isActive ? 'text-emerald-500' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                  {isBecomeMemberSectionOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                              </div>

                              {/* Show content only when Become Member section is open */}
                              {isBecomeMemberSectionOpen && (
                                <div className="ml-6 space-y-1 border-l border-emerald-100 pl-3 py-1">

                                  {/* Static Children FIRST: Reasons to Join, Join Today, Code of Conduct */}
                                  {item.children && item.children.map((child) => (
                                    <NavLink
                                      key={child.id}
                                      to={child.path}
                                      onClick={() => isMobile && setIsSidebarOpen(false)}
                                      className={({ isActive }) => `flex items-center px-3 py-1.5 text-[12px] rounded transition-all mb-1
                                      ${isActive 
                                        ? 'text-emerald-600 font-medium bg-emerald-50' 
                                        : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                                      }`}
                                    >
                                      <ChevronRight className="w-3 h-3 mr-2 text-gray-400" />
                                      <span className="break-words whitespace-normal">{child.text}</span>
                                    </NavLink>
                                  ))}

                                  {/* Separator between static and dynamic items */}
                                  {becomeMemberButtons.length > 0 && item.children && item.children.length > 0 && (
                                    <div className="my-2 border-t border-gray-100"></div>
                                  )}

                                  {/* Dynamic buttons from API */}
                                  <div className="space-y-1">
                                    {/* Loading state */}
                                    {isLoadingBecomeMember && becomeMemberButtons.length === 0 && (
                                      <div className="px-3 py-1.5 text-[12px] text-gray-400">
                                        Loading dynamic content...
                                      </div>
                                    )}

                                    {/* No buttons state */}
                                    {!isLoadingBecomeMember && becomeMemberButtons.length === 0 && (
                                      <div className="px-3 py-1.5 text-[12px] text-gray-400">
                                        No dynamic content available
                                      </div>
                                    )}

                                    {/* Dynamic buttons from API */}
                                    {becomeMemberButtons.map((button, index) => {
                                      const isButtonActive = isBecomeMemberContentActive(button.slug);

                                      return (
                                        <div
                                          key={index}
                                          onClick={() => handleBecomeMemberButtonClick(button)}
                                          className={`flex items-center justify-between px-3 py-1.5 text-[12px] rounded transition-all cursor-pointer
                                          ${
                                            isButtonActive
                                              ? 'text-emerald-600 font-medium bg-emerald-50' 
                                              : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                                          }`}
                                        >
                                          <div className="flex items-center">
                                            <CheckSquare className="w-3 h-3 mr-2 text-emerald-500 flex-shrink-0" />
                                            <span className="break-words whitespace-normal">{button.text}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {button.hasContent && (
                                              <BookOpen className="w-3 h-3 text-emerald-400" title="Has additional content" />
                                            )}
                                            <ChevronRight className="w-3 h-3 text-gray-300" />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        }

                        // For other menu items (original behavior)
                        return (
                          <div key={item.id} className="relative">
                            {item.hasChildren ? (
                              <>
                                <div
                                  className={`flex items-center justify-between pr-2 rounded-r-lg transition-all hover:bg-gray-50
                                    ${isActive ? 'bg-emerald-50' : ''}`}
                                >
                                  <NavLink
                                    to={item.path}
                                    onClick={() => isMobile && setIsSidebarOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all flex-1 min-w-0
                                    ${isActive ? 'text-emerald-600 font-bold' : 'text-gray-500 hover:text-emerald-600'}`}
                                  >
                                    {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                                    <span className="break-words whitespace-normal">{item.text}</span>
                                  </NavLink>
                                  <button
                                    onClick={(e) => toggleSubmenu(item.id, e)}
                                    className={`p-1 rounded hover:bg-emerald-100 flex-shrink-0 ${isActive ? 'text-emerald-500' : 'text-gray-400 hover:text-gray-600'}`}
                                  >
                                    {isSubmenuOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                  </button>
                                </div>

                                {isSubmenuOpen && item.children && (
                                  <div className="ml-6 space-y-1 border-l border-emerald-100 pl-3 py-1">
                                    {item.children.map((child) => (
                                      <NavLink
                                        key={child.id}
                                        to={child.path}
                                        onClick={() => isMobile && setIsSidebarOpen(false)}
                                        className={({ isActive }) => `block px-3 py-1.5 text-[12px] rounded transition-all
                                        ${isActive 
                                          ? 'text-emerald-600 font-medium bg-emerald-50' 
                                          : 'text-gray-400 hover:text-emerald-600 hover:bg-emerald-50'
                                        }`}
                                      >
                                        • <span className="break-words whitespace-normal">{child.text}</span>
                                      </NavLink>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <NavLink
                                to={item.path}
                                onClick={() => isMobile && setIsSidebarOpen(false)}
                                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all
                                ${isActive 
                                  ? 'text-emerald-600 font-bold bg-emerald-50' 
                                  : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'
                                }`}
                              >
                                {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                                <span className="break-words whitespace-normal">{item.text}</span>
                              </NavLink>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DLA Trade Section */}
            <div className="pt-2">
              <div className={`relative rounded-xl transition-all mb-1
                ${isAnyTradeRouteActive() 
                  ? 'bg-emerald-50 border border-emerald-100 shadow-lg shadow-emerald-200/50' 
                  : 'bg-gray-50 border border-gray-100 hover:bg-gray-100 hover:shadow-lg'
                }`}
              >
                <NavLink
                  to="/DlaTrade/headline-sponsor"
                  onClick={() => isMobile && setIsSidebarOpen(false)}
                  className="flex items-center justify-between px-4 py-3 no-underline cursor-pointer"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 rounded-lg shadow-lg ${isAnyTradeRouteActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-200 text-gray-600 shadow-gray-300/50'}`}>
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium drop-shadow-sm ${isAnyTradeRouteActive() ? 'text-emerald-600' : 'text-gray-800'} break-words whitespace-normal`}>
                        DLA Trade
                      </span>
                    </div>
                  </div>
                  <div
                    className={`transition-transform flex-shrink-0 ml-2 ${isAnyTradeRouteActive() ? 'text-emerald-500' : 'text-gray-500'}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleTradeSection();
                    }}
                  >
                    {isTradeSectionOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </NavLink>

                {isTradeSectionOpen && (
                  <div className="pb-2">
                    <div className="ml-10 mr-3 border-l border-gray-200 space-y-1">
                      {tradeMenuItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = isActivePath(item.path);

                        return (
                          <div key={item.id} className="relative">
                            <NavLink
                              to={item.path}
                              onClick={() => isMobile && setIsSidebarOpen(false)}
                              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-[13px] rounded-r-lg transition-all
                              ${isActive 
                                ? 'text-emerald-600 font-bold bg-emerald-50' 
                                : 'text-gray-500 hover:text-emerald-600 hover:bg-gray-50'
                              }`}
                            >
                              {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                              <span className="break-words whitespace-normal">{item.text}</span>
                            </NavLink>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ==================== ADD STORE CARDS BUTTON - EXACT SAME STYLE AS THE DENTALLAB JOURNAL ==================== */}
            <NavLink
              to="/add-store-cards"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isAddStoreCardsActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <CreditCard className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">Add Store Cards</span>
            </NavLink>

            {/* ==================== NEW: DRAP LICENCE APPLICATIONS BUTTON ==================== */}
            <NavLink
              to="/drap-applications"
              onClick={() => isMobile && setIsSidebarOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl transition-all
              ${isActive 
                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100 shadow-lg shadow-emerald-200/50'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 shadow-md hover:shadow-lg'}
              `}
            >
              <div className={`p-2 rounded-lg shadow-lg ${isDrapApplicationsActive() ? 'bg-emerald-500 text-white shadow-emerald-500/50' : 'bg-gray-100 text-gray-600 shadow-gray-300/50'}`}>
                <ClipboardList className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium drop-shadow-sm">DRAP Licence Applications</span>
            </NavLink>

          </nav>

          {/* Logout */}
          <div className="pt-6 mt-6 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl
              bg-gray-50 hover:bg-red-50 text-gray-700 hover:text-red-600 
              border border-gray-200 hover:border-red-200 
              shadow-lg hover:shadow-xl hover:shadow-red-200/30 transition-all duration-300"
            >
              <div className="p-2 rounded-lg bg-gray-100 text-gray-600 shadow-md flex-shrink-0">
                <LogOut className="w-4 h-4" />
              </div>
              <div className="text-left min-w-0">
                <span className="text-sm font-medium block drop-shadow-sm break-words whitespace-normal">Logout</span>
                <span className="text-xs text-gray-500 drop-shadow-sm break-words whitespace-normal">Sign out from system</span>
              </div>
            </button>
          </div>
        </div>

        {/* Ultra-minimal scrollbar styling */}
        <style jsx>{`
          /* Hide the main sidebar container scrollbar */
          div[class*="flex flex-col w-72"] {
            scrollbar-width: none; /* Firefox */
            -ms-overflow-style: none; /* IE and Edge */
          }
          
          div[class*="flex flex-col w-72"]::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
          
          /* Style the scrollable content area */
          div[class*="p-5 h-full flex flex-col"] {
            /* Custom scrollbar for Webkit browsers (Chrome, Safari, Edge) */
            scrollbar-width: thin;
            scrollbar-color: rgba(209, 213, 219, 0.2) transparent;
          }
          
          div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar {
            width: 3px; /* Ultra thin - 3px */
            height: 3px;
          }
          
          div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-track {
            background: transparent;
            margin: 2px 0;
          }
          
          div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb {
            background: rgba(209, 213, 219, 0.25); /* Very light grey, almost invisible */
            border-radius: 10px;
            transition: all 0.2s ease;
          }
          
          div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb:hover {
            background: rgba(209, 213, 219, 0.35); /* Slightly more visible on hover */
          }
          
          div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb:active {
            background: rgba(209, 213, 219, 0.45);
          }
          
          /* Firefox scrollbar */
          div[class*="p-5 h-full flex flex-col"] {
            scrollbar-width: thin;
            scrollbar-color: rgba(209, 213, 219, 0.2) transparent;
          }
          
          /* Edge scrollbar */
          @supports (-ms-ime-align: auto) {
            div[class*="p-5 h-full flex flex-col"] {
              -ms-overflow-style: -ms-autohiding-scrollbar;
            }
          }
          
          /* Mobile - even thinner */
          @media (max-width: 768px) {
            div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar {
              width: 2px;
            }
            
            div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb {
              background: rgba(209, 213, 219, 0.2);
            }
          }
          
          /* For dark mode (optional) */
          @media (prefers-color-scheme: dark) {
            div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb {
              background: rgba(156, 163, 175, 0.2);
            }
            
            div[class*="p-5 h-full flex flex-col"]::-webkit-scrollbar-thumb:hover {
              background: rgba(156, 163, 175, 0.3);
            }
          }
        `}</style>
      </div>

      {/* Mobile overlay */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 shadow-2xl"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;